import React, {useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types';
import {useSelector} from "react-redux";
import {Button, Col, Nav, OverlayTrigger, Row, Tooltip} from 'react-bootstrap'
import {isEmpty} from "lodash";
import TimelineReferral from './TimelineReferral';
import {useReferralsContext} from '../context/Referrals.context';
import SortMenu from "./SortMenu";
import {sortItems} from "../constants";
import {SortParam} from "../../../../dtos/PatientSearchDTO";
import SpinnerLoading from "../../../common/components/spinner";

function HeaderTabs({patient, reachedBottom, setReachedBottom}) {
  const {
    refreshToken,
    getMyMembersReferrals,
    scrollMyMembersReferrals,
    referralsList,
    totalSize,
    isLoading,
    hasMore
  } = useReferralsContext();

  const userEventAction = useSelector(
    (state) => state?.manageVisitReducer?.userEventAction);
  const defaultSortParams = useMemo(() => [new SortParam("statusupdated", "DESC", 1)], []);
  const [sortParams, setSortParams] = useState(defaultSortParams)
  const [selectedTab, setSelectedTab] = useState('all')
  const [openStates, setOpenStates] = useState([]);
  const [expandAll, setExpandAll] = useState(false);
  const patientId = patient?.id;
  const resetReachedBottom = useCallback(() => {
    if (typeof setReachedBottom === 'function') {
      setReachedBottom(false);
    }
  }, [setReachedBottom]);

  const getSortParamsOrDefault = useCallback(() => {
    return isEmpty(sortParams) ? defaultSortParams : sortParams;
  }, [defaultSortParams, sortParams]);

  const getFiltersParamsByTab = useCallback((tab) => {
    if (tab === 'sent') {
      return [{
        paramName: "Data Type",
        paramValue: ["sent"],
        comparison: "eq",
        displayValue: ["Sent"]
      }];
    } else if (tab === 'received') {
      return [{
        paramName: "Data Type",
        paramValue: ["received"],
        comparison: "eq",
        displayValue: ["Received"]
      }];
    }
    return [];
  }, []);

  const activeReferrals = useMemo(() => {
    if (selectedTab === 'sent') {
      return referralsList?.filter(e => e?.dataType === 'sent') ?? [];
    }
    if (selectedTab === 'received') {
      return referralsList?.filter(e => e?.dataType === 'received') ?? [];
    }
    return referralsList?.filter(e => e?.showAll !== false) ?? [];
  }, [referralsList, selectedTab]);

  useEffect(() => {
    if (patientId) {
      resetReachedBottom();
      setExpandAll(false);
      getMyMembersReferrals(patientId, getFiltersParamsByTab(selectedTab), getSortParamsOrDefault());
    }
    // Refetch when patient, tab, sort, or saved referral status changes; context callbacks are recreated after dispatch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, refreshToken, resetReachedBottom, selectedTab, sortParams])

  useEffect(() => {
    if (activeReferrals) {
      setOpenStates(prevStates => {
        const newStates = new Array(activeReferrals.length).fill(expandAll);
        prevStates.forEach((state, index) => {
          if (index < newStates.length) {
            newStates[index] = state;
          }
        });
        return newStates;
      });
    }
  }, [activeReferrals, expandAll])

  useEffect(() => {
    if (userEventAction?.data === 'REFERRAL_ADDED') {
      resetReachedBottom();
      getMyMembersReferrals(patientId, getFiltersParamsByTab(selectedTab), getSortParamsOrDefault());
    }
    // React to referral-added events without subscribing to helper identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, resetReachedBottom, selectedTab, sortParams, userEventAction])

  useEffect(() => {
    if (activeReferrals.length > 0 && reachedBottom && !isLoading && hasMore) {
      scrollMyMembersReferrals(patientId)
        .then(() => {
          resetReachedBottom();
        });
    }
    // Scroll callback identity changes with context state; this should run only from the drawer bottom signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReferrals.length, hasMore, isLoading, patientId, reachedBottom, resetReachedBottom])

  const onSelectTab = (tab) => {
    // we update the state to force the rendering of the tabs and thus meet the acceptance criteria:
    // AC: any expanded card must return to its original state (contracted)
    setOpenStates([]);
    setSelectedTab(tab);
  }

  const onSelectSort = (selectedSort) => {
    setSortParams(selectedSort);
    setOpenStates([]);
  }

  const toggleAll = (state) => {
    setExpandAll(state)
    setOpenStates(prev => prev.map(() => state));
  };

  const toggleIndividual = (index) => {
    setOpenStates(prev => prev.map((state, i) => i === index ? !state : state));
  };

  const toggleIndividualOpen = (index) => {
    setOpenStates(prev => prev.map((state, i) => i === index ? true : state));
  };

  return (
    <>
      <div className="stickyscroll bg-light">
        <Nav variant="tabs" as="nav" className="mb-3 patientdrawer-tabs align-items-center" activeKey={selectedTab} onSelect={onSelectTab}>
          <Nav.Link eventKey="all" className="nav-item rounded-start">All</Nav.Link>
          <Nav.Link eventKey="sent" className="nav-item rounded-0">Sent</Nav.Link>
          <Nav.Link eventKey="received" className="nav-item rounded-start-0 rounded-end">Received</Nav.Link>
          <SortMenu
            items={sortItems}
            sortParams={getSortParamsOrDefault()}
            setSortParams={onSelectSort}
          />
        </Nav>
        <Row className='align-items-baseline'>
          <Col>
              <span
                id={`assessmentsEventsCount`}
                className="border text-dark badge badge-pill mt-2">
                {totalSize}{" "}{"Referral"}{totalSize === 1 ? '' : 's'}
              </span>
          </Col>
          <Col md={6} className='text-end'>
            <OverlayTrigger
              placement="auto"
              overlay={
                <Tooltip>
                  Expand / Collapse All
                </Tooltip>
              }
            >
              <Button
                variant="light"
                aria-label="Expand or collapse all referrals"
                className="border text-dark badge badge-pill mt-2 font-sm"
                onClick={() => toggleAll(!expandAll)}
                style={{maxWidth: 'fit-content'}}
              >
                <i className='fas fa-angle-double-up'/>
                <i className='fas fa-angle-double-down'/>
              </Button>
            </OverlayTrigger>
          </Col>
        </Row>
      </div>
      <TimelineReferral
        items={activeReferrals}
        patient={patient}
        toggleIndividual={toggleIndividual}
        openStates={openStates}
        toggleIndividualOpen={toggleIndividualOpen}
      />
      {isLoading && <SpinnerLoading />}
      {!hasMore && activeReferrals.length > 0 && (
        <div className="d-flex w-100 justify-content-center">
          <p>You have reached the bottom</p>
        </div>
      )}
    </>
  )
}

HeaderTabs.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  reachedBottom: PropTypes.bool,
  setReachedBottom: PropTypes.func,
};

export default HeaderTabs
